import { IconButton } from "@carbon6/c6-frontend-design";
import { useEffect, useState } from "react";
import { useAppState } from "@/app";
import { issueConnectCode, type ConnectCode } from "@/auth";
import { CopyButton } from "@/components/buttons";
import { appConfig } from "@/config";
import { isHttpClientError } from "@/http";
import { createLogger } from "@/logging";
import { parseUtcMs } from "@/shared/utils/datetime/public";
import styles from "./McpConnectPanel.module.css";

const logger = createLogger("settings/mcp-connect");

// 등록할 MCP 서버 이름이다. 사용자가 에이전트에서 이 이름으로 호출한다.
const SERVER_NAME = "hatchery";

function codexBrowserCommand(mcpUrl: string): string {
  return [
    "codex mcp add " + SERVER_NAME + " --url " + mcpUrl,
    "codex mcp login " + SERVER_NAME,
  ].join("\n");
}

function claudeCommand(mcpUrl: string): string {
  return "claude mcp add --transport http " + SERVER_NAME + " " + mcpUrl;
}

// 남은 시간 표시다. 안내가 아니라 현재 값이므로 짧은 명사형으로 쓴다.
function remainingLabel(remainingMs: number): string {
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(seconds / 60);
  return minutes > 0 ? minutes + "분 " + (seconds % 60) + "초" : seconds + "초";
}

// 만료는 절대 시각으로 계산한다. 그래서 모달을 열어 둔 채 시간이 지나도 남은 시간이 어긋나지 않는다.
// 시각을 읽지 못하면 이미 만료된 것으로 판정하고 재발급을 안내한다. 만료됐을 수 있는 코드를
// 유효하다고 표시하지 않는다.
function expiryTimestamp(issued: ConnectCode): number {
  return parseUtcMs(issued.expiresAt) ?? 0;
}

// 발급 실패 안내다. 인증 서버가 사유를 code 로 준다. 그 code 에 따라 다음에 할 일이 다르다.
function issueErrorMessage(error: unknown): string {
  if (isHttpClientError(error)) {
    switch (error.code) {
      case "auth.bearer.invalid":
        return "로그인이 만료되었습니다. 다시 로그인한 뒤 발급해 주세요.";
      case "auth.otp.ineligible":
        return "이 계정은 현재 Registry 멤버가 아닙니다. 관리자에게 문의해 주세요.";
      case "auth.rate_limit.exceeded":
        return "발급 요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.";
    }
  }
  return "연결 코드를 발급하지 못했습니다. 잠시 후 다시 시도해 주세요.";
}

// Codex 연결 카드다. 기본 경로는 연결 코드다. Codex 의 OAuth 콜백은 자기 머신의 loopback 이다.
// 그래서 브라우저가 다른 머신에 있으면 완료되지 않는다. 원격 셸에서 연결하는 경우가 더 많다.
function CodexSetup({ mcpUrl }: { mcpUrl: string }) {
  const { state } = useAppState();
  const accessToken = state.auth.status === "signedIn" ? state.auth.session.accessToken : null;
  const [issued, setIssued] = useState<ConnectCode | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [remainingMs, setRemainingMs] = useState(0);

  // 발급된 코드가 있는 동안만 1초 간격으로 남은 시간을 다시 그린다.
  useEffect(() => {
    if (issued === null) {
      return;
    }
    const expiresAt = expiryTimestamp(issued);
    let timer = 0;
    const tick = (): void => {
      const left = expiresAt - Date.now();
      setRemainingMs(left);
      if (left <= 0) {
        window.clearInterval(timer);
      }
    };
    tick();
    timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [issued]);

  async function handleIssue(): Promise<void> {
    if (accessToken === null) {
      setIssueError("로그인한 뒤에 연결 코드를 발급할 수 있습니다.");
      return;
    }
    setIssuing(true);
    setIssueError(null);
    try {
      // 인증 서버는 Bearer 를 자동으로 받지 않는다. 현재 세션의 토큰을 명시해 전달한다.
      setIssued(await issueConnectCode(accessToken));
    } catch (error: unknown) {
      logger.warn("Failed to issue connect code", { error });
      setIssueError(issueErrorMessage(error));
    } finally {
      setIssuing(false);
    }
  }

  const expired = issued !== null && remainingMs <= 0;

  return (
    <article className={styles.setupCard}>
      <div className={styles.row}>
        <div className={styles.text}>
          <p className={styles.label}>Codex</p>
          <p className={styles.desc}>
            브라우저를 열 수 없는 원격 터미널에서도 연결됩니다. 연결 코드를 발급받아 터미널에
            붙여넣어 주세요.
          </p>
        </div>
        <div className={styles.control}>
          <IconButton
            text={issuing ? "발급하는 중…" : issued === null ? "연결 코드 발급" : "다시 발급"}
            type="button"
            variant="solid"
            disabled={issuing}
            onClick={() => void handleIssue()}
          />
        </div>
      </div>

      {issueError !== null && <p className={styles.connectError}>{issueError}</p>}

      {issued !== null &&
        (expired ? (
          // 만료된 코드는 명령과 함께 숨긴다. 사용자가 만료된 코드를 복사해 붙여넣지 않게 한다.
          <p className={styles.connectError}>연결 코드가 만료되었습니다. 다시 발급해 주세요.</p>
        ) : (
          <div className={styles.connect}>
            <div className={styles.row}>
              <div className={styles.text}>
                <p className={styles.connectCode}>{issued.code}</p>
                <p className={styles.connectMeta}>
                  {remainingLabel(remainingMs)} 뒤에 만료되며 한 번만 사용할 수 있습니다.
                </p>
              </div>
              <div className={styles.control}>
                <CopyButton text={issued.installCommand} ariaLabel="Codex 연결 명령 복사" />
              </div>
            </div>
            <pre className={styles.code}>
              <code>{issued.installCommand}</code>
            </pre>
          </div>
        ))}

      {mcpUrl !== "" && (
        <div className={styles.fallback}>
          <p className={styles.fallbackLabel}>이 컴퓨터에서 브라우저를 열 수 있다면</p>
          <div className={styles.row}>
            <p className={styles.desc}>
              서버를 등록한 뒤 브라우저로 승인합니다. CLI와 IDE가 같은 설정을 씁니다.
            </p>
            <div className={styles.control}>
              <CopyButton text={codexBrowserCommand(mcpUrl)} ariaLabel="Codex 설정 복사" />
            </div>
          </div>
          <pre className={styles.code}>
            <code>{codexBrowserCommand(mcpUrl)}</code>
          </pre>
        </div>
      )}
    </article>
  );
}

// MCP 연결 안내 패널이다. 첫 화면에는 연결 수단만 두고, 기능·인증 설명은 스크롤해야 보이는 위치에 둔다.
// 기능 경계의 SoT는 hatchery-registry-backend docs/specs/mcp-gateway.md §5-6과
// hatchery_mcp/tools/dataset_read.py다. 연결 코드(OTP) 계약은 hatchery-overlord
// docs/specs/authentication-and-identity.md가 소유한다.
export function McpConnectPanel() {
  const mcpUrl = appConfig.mcpUrl;

  return (
    <div className={styles.body}>
      <section className={styles.quickStart} aria-labelledby="mcp-quick-start-title">
        <header className={styles.header}>
          <h3 className={styles.title}>MCP 연결</h3>
          <p className={styles.subtitle}>
            Codex 또는 Claude Code에서 Hatchery Registry를 바로 사용할 수 있습니다.
          </p>
        </header>

        <div className={styles.quickStartHeading}>
          <p className={styles.eyebrow}>TL;DR</p>
          <h4 id="mcp-quick-start-title" className={styles.heading}>
            연결 명령을 실행하세요
          </h4>
        </div>

        <div className={styles.setupList}>
          <CodexSetup mcpUrl={mcpUrl} />

          {mcpUrl === "" ? (
            <p className={styles.notice}>
              이 환경에는 MCP 게이트웨이 주소가 설정되어 있지 않아 Claude Code 명령을 보여 드릴 수
              없습니다. 관리자에게 문의해 주세요.
            </p>
          ) : (
            <article className={styles.setupCard}>
              <div className={styles.row}>
                <div className={styles.text}>
                  <p className={styles.label}>Claude Code</p>
                  <p className={styles.desc}>서버 등록과 OAuth 로그인을 한 번에 시작합니다.</p>
                </div>
                <div className={styles.control}>
                  <CopyButton text={claudeCommand(mcpUrl)} ariaLabel="Claude Code 설정 복사" />
                </div>
              </div>
              <pre className={styles.code}>
                <code>{claudeCommand(mcpUrl)}</code>
              </pre>
            </article>
          )}
        </div>

        <p className={styles.scrollHint}>기능과 권한 안내는 아래로 스크롤해 확인하세요.</p>
      </section>

      <div className={styles.details}>
        <section className={styles.section} aria-labelledby="mcp-capabilities-title">
          <h4 id="mcp-capabilities-title" className={styles.heading}>
            무엇을 할 수 있나요?
          </h4>

          <article className={styles.featureCard}>
            <p className={styles.featureTitle}>벡터 신호로 Dataset 찾기</p>
            <p className={styles.featureDescription}>
              <code>dataset_search</code>에서 먼저 <code>mode=&quot;vector_channels&quot;</code>로
              채널을 확인하고, <code>vector_channel=&quot;fsr&quot;</code> 또는
              <code>action:ee_pose</code>처럼 신호·namespace로 좁힐 수 있습니다.
              <code>vector_channel_count</code>와 <code>structure_signature</code>를 함께 쓰면 채널
              구성이 맞는 Dataset만 모을 수 있습니다.
            </p>
          </article>

          <ul className={styles.list}>
            <li>Dataset·에피소드·진단·메모·파일 메타를 조회하고 signed URL로 파일에 접근</li>
            <li>업로드 lifecycle과 Annotation set·frame·tracking 작업</li>
            <li>Training·Evaluation 조회, Training 제출, Session·Policy·실험 제어</li>
          </ul>
          <p className={styles.desc}>
            검색에 쓴 벡터·구조 조건은 Training 제출의 <code>dataset_filter</code> 축으로도 사용할
            수 있고, 제출 전 선택된 Dataset을 미리 확인할 수 있습니다.
          </p>
        </section>

        <section className={styles.section} aria-labelledby="mcp-safety-title">
          <h4 id="mcp-safety-title" className={styles.heading}>
            권한과 안전장치
          </h4>
          <ul className={styles.list}>
            <li>에이전트는 내 Registry 권한을 그대로 사용합니다.</li>
            <li>상태 변경은 먼저 계획을 보여 주고, 확인한 뒤에만 적용합니다.</li>
            <li>Evaluation 기능은 research group 멤버만, 관리자 전용 기능은 사용할 수 없습니다.</li>
          </ul>
        </section>

        <section className={styles.section} aria-labelledby="mcp-auth-title">
          <h4 id="mcp-auth-title" className={styles.heading}>
            인증
          </h4>
          <ul className={styles.list}>
            <li>허용된 GitHub 조직의 멤버만 연결할 수 있습니다.</li>
            <li>연결 코드는 내 계정으로 발급되며, 내 권한이 그대로 따라갑니다.</li>
            <li>연결은 3개월 뒤 만료됩니다. 만료되면 같은 방법으로 다시 연결해 주세요.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
